import { apiEndpoints } from '@/lib/api/endpoints'
import { baseApi } from './baseQuery'
import { enrichPricing } from '@/components/Store/StoreUtilities'
import { Sku } from '@/types/interfaces/sku'
import type { StoreUniversalSearchResponse } from '@/types/interfaces/storeUniversalSearch'
import { RenderedService, ServiceItem } from '@/types/interfaces/service-item'
import { StoreAnalytics, trackStoreEvent } from '@nile/mantine-theme/analytics'

const transformSku = (sku: Sku): ServiceItem => ({
    id: sku.id,
    title: sku.name,
    description: sku.description,
    category: sku.category as any,
    skuId: sku.id,
    skuName: sku.hyphenated_name || sku.name.toLowerCase().replace(/\s+/g, '-'),
    imageUrl: sku.s3_object_key || '',
    primaryPrice: '',
    secondaryPrice: '',
    originType: 'chat',
    price: {},
})

/** Rails returns 403 with this body when the SKU is disabled for the state. */
export interface SkuStateAvailabilityResponse {
    available?: boolean
    error?: string
    message?: string
    sku_category?: string | null
}

interface CategoryResponse {
    raw?: {
        renderedServices?: RenderedService[]
    }
    authenticated?: boolean
}

function extractUniqueServices(renderedServices: RenderedService[]): ServiceItem[] {
    const allServices = renderedServices.flatMap((rs) => rs.services || [])
    const uniqueById = new Map<string, ServiceItem>()
    for (const svc of allServices) {
        if (svc.skuName && svc.skuId && !uniqueById.has(svc.skuId)) {
            uniqueById.set(svc.skuId, svc)
        }
    }
    return Array.from(uniqueById.values())
}

export const skuApi: any = baseApi.injectEndpoints({
    endpoints: (builder) => ({
        getSku: builder.query<Sku, string>({
            query: (skuId) => apiEndpoints.skus.getById(skuId),
        }),

        // One-off availability check at location confirmation; not cached state, so no tags.
        checkSkuStateAvailability: builder.query<SkuStateAvailabilityResponse, { skuId: string; state: string }>({
            query: ({ skuId, state }) => apiEndpoints.skus.checkStateAvailability(skuId, state),
        }),

        getSkus: builder.query<ServiceItem[], string[]>({
            queryFn: async (skuIds, { dispatch }) => {
                const results = await Promise.allSettled(
                    skuIds.map((id) => dispatch(skuApi.endpoints.getSku.initiate(id)).unwrap()),
                )

                const skus = results
                    .filter((result): result is PromiseFulfilledResult<Sku> => result.status === 'fulfilled')
                    .map((result) => transformSku(result.value))

                return { data: skus }
            },
        }),

        getCategoryServices: builder.query<ServiceItem[], { slug: string }>({
            query: ({ slug }) => apiEndpoints.category.getBySlug(slug),
            transformResponse: (response: CategoryResponse) => {
                const renderedServices = response.raw?.renderedServices || []
                return extractUniqueServices(renderedServices)
            },
            providesTags: (_result, _error, { slug }) => [{ type: 'Sku', id: `category-${slug}` }],
        }),

        getAllCareServices: builder.query<Record<string, ServiceItem[]>, string[]>({
            queryFn: async (slugs, { dispatch }) => {
                const results = await Promise.allSettled(
                    slugs.map((slug) => dispatch(skuApi.endpoints.getCategoryServices.initiate({ slug })).unwrap()),
                )

                const data: Record<string, ServiceItem[]> = {}
                slugs.forEach((slug, i) => {
                    const result = results[i]
                    data[slug] = result.status === 'fulfilled' ? result.value : []
                })

                return { data }
            },
            providesTags: ['Sku'],
        }),

        getSkuSearch: builder.query<
            StoreUniversalSearchResponse,
            {
                query: string
                includeProviders?: boolean
                includePharmacies?: boolean
                zipcode?: string | null
                latitude?: number
                longitude?: number
                skuCategory?: string
            }
        >({
            query: ({ query, includeProviders, includePharmacies, zipcode, latitude, longitude, skuCategory }) =>
                apiEndpoints.storeSearch.index({
                    query,
                    includeProviders,
                    includePharmacies,
                    zipcode,
                    latitude,
                    longitude,
                    skuCategory,
                }),
            transformResponse: (response: StoreUniversalSearchResponse) => {
                if (!response?.results) {
                    throw new Error('Missing search results')
                }

                enrichPricing(response.results, Boolean(response.authenticated))
                return response
            },
            async onQueryStarted({ query }, { queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled
                    trackStoreEvent(StoreAnalytics.VIEWED_SEARCH_RESULTS, {
                        query,
                        search_results_length: data.results.length,
                        searchResults: data.results.map(({ skuId, title }) => ({ skuId, title })),
                    })
                } catch {
                    trackStoreEvent(StoreAnalytics.VIEWED_SEARCH_RESULTS_FAILURE, {
                        query,
                    })
                }
            },
        }),

        getSearchSuggestions: builder.query<string[], { query: string }>({
            query: ({ query }) => apiEndpoints.storeSearch.suggestions(query),
            providesTags: ['SearchSuggestions'],
            // Same prefix is often typed again after the panel closes.
            keepUnusedDataFor: 300,
        }),
    }),
})

export const {
    useGetSkuQuery,
    useLazyCheckSkuStateAvailabilityQuery,
    useGetSkusQuery,
    useGetCategoryServicesQuery,
    useGetAllCareServicesQuery,
    useGetSkuSearchQuery,
    useGetSearchSuggestionsQuery,
} = skuApi
